// LearnCore Frontend - 奖励列表组件（儿童友好版本）

import React, { useState, useEffect } from 'react';
import { rewardsApi } from '../api/tasks';
import RewardCard from './RewardCard';
import type {
  RewardTemplate,
  RewardType,
  GetRewardsRequest
} from '../types/tasks-rewards';

interface RewardListProps {
  userId?: string;
  filters?: {
    type?: RewardType[];
    min_dkp_cost?: number;
    max_dkp_cost?: number;
    available_only?: boolean;
  };
  showUserDKP?: boolean;
  compact?: boolean;
  onRedeem?: (rewardId: string) => void;
  className?: string;
}

export const RewardList: React.FC<RewardListProps> = ({
  userId,
  filters,
  showUserDKP = true,
  compact = false,
  onRedeem,
  className = ''
}) => {
  const [rewards, setRewards] = useState<RewardTemplate[]>([]);
  const [userDKP, setUserDKP] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(0);

  const loadRewards = async (reset = false) => {
    try {
      setLoading(true);
      setError(null);

      const request: GetRewardsRequest = {
        type: filters?.type,
        is_active: filters?.available_only !== false,
        user_id: userId,
        limit: 20,
        offset: reset ? 0 : page * 20
      };

      const response = await rewardsApi.getRewards(request);

      if (reset) {
        setRewards(response.rewards || []);
        setPage(0);
      } else {
        setRewards(prev => [...prev, ...(response.rewards || [])]);
      }

      setHasMore(response.has_more);
    } catch (err) {
      setError('加载奖励失败');
      console.error('Failed to load rewards:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadUserDKP = async () => {
    if (!showUserDKP || !userId) return;

    try {
      // DKP余额已在 getRewards 返回中包含，此处保持兼容
      setUserDKP(0);
    } catch (err) {
      console.error('Failed to load user DKP:', err);
    }
  };

  useEffect(() => {
    loadRewards(true);
    loadUserDKP();
  }, [userId, filters]);

  const handleRedeem = (rewardId: string) => {
    // 重新加载DKP余额
    loadUserDKP();

    if (onRedeem) {
      onRedeem(rewardId);
    }
  };

  const loadMore = () => {
    if (!loading && hasMore) {
      setPage(prev => prev + 1);
      loadRewards(false);
    }
  };

  const filterByDKPCost = (reward: RewardTemplate) => {
    if (!filters?.min_dkp_cost && !filters?.max_dkp_cost) return true;

    if (filters?.min_dkp_cost && reward.dkp_cost < filters.min_dkp_cost) return false;
    if (filters?.max_dkp_cost && reward.dkp_cost > filters.max_dkp_cost) return false;

    return true;
  };

  const filteredRewards = (rewards || []).filter(filterByDKPCost);

  if (loading && (!rewards || rewards.length === 0)) {
    return (
      <div className={`grid ${compact ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'} gap-4 ${className}`}>
        {[...Array(6)].map((_, i) => (
          <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="animate-pulse">
              <div className="h-32 bg-gray-200 dark:bg-gray-700"></div>
              <div className="p-4">
                <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-1/2 mb-3"></div>
                <div className="h-6 bg-gray-200 dark:bg-gray-600 rounded w-1/3"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 ${className}`}>
        <div className="text-red-800 dark:text-red-400">{error}</div>
        <button
          onClick={() => loadRewards(true)}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
        >
          重试
        </button>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* DKP余额显示 - 儿童友好版本 */}
      {showUserDKP && (
        <div className="mb-8">
          <div className="bg-gradient-to-r from-orange-400 via-pink-400 to-purple-400 rounded-2xl p-6 text-white shadow-lg transform hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm opacity-90">🌟 我的金币余额</div>
                <div className="text-4xl font-bold">{userDKP}</div>
              </div>
              <div className="text-right">
                <div className="text-sm opacity-90">🎁 可兑换奖励</div>
                <div className="text-2xl font-bold">
                  {filteredRewards.filter(r => r.dkp_cost <= userDKP).length} 个
                </div>
              </div>
            </div>

            {/* 激励文字 */}
            <div className="text-center mt-3 text-sm opacity-90">
              {filteredRewards.filter(r => r.dkp_cost <= userDKP).length > 0
                ? '🎉 太棒了！你现在可以兑换这些奖励啦！'
                : '💪 继续完成任务，赚取金币，解锁更多精彩奖励！'}
            </div>
          </div>
        </div>
      )}

      {/* 奖励列表 - 单列布局 */}
      {!filteredRewards || filteredRewards.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-16 text-center">
          <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center animate-bounce">
            <span className="text-5xl">🎁</span>
          </div>
          <div className="text-2xl font-bold text-gray-600 dark:text-gray-400 mb-4">
            暂无可用奖励
          </div>
          <div className="text-lg text-gray-500 dark:text-gray-500 mb-6">
            {filters?.min_dkp_cost || filters?.max_dkp_cost
              ? '😊 尝试调整筛选条件，发现更多精彩奖励！'
              : '✨ 快去完成任务赚取金币，解锁更多奖励吧！'}
          </div>

          {/* 提示步骤 */}
          <div className="flex justify-center gap-6">
            <div className="text-center">
              <div className="text-3xl mb-2">✅</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">完成任务</div>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-2">💰</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">获得金币</div>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-2">🎁</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">兑换奖励</div>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* 单列奖励列表 */}
          <div className="max-w-3xl mx-auto space-y-6">
            {filteredRewards && filteredRewards.map((reward, index) => (
              <div
                key={reward.id}
                className="transform transition-all duration-500"
                style={{
                  animation: `slideInUp 0.6s ease-out ${index * 0.15}s both`
                }}
              >
                <RewardCard
                  reward={reward}
                  userBalance={userDKP}
                  showUserDKP={showUserDKP}
                  onRedeem={handleRedeem}
                />
              </div>
            ))}
          </div>

          {/* 加载更多按钮 - 儿童友好版本 */}
          {hasMore && (
            <div className="text-center py-8">
              <button
                onClick={loadMore}
                disabled={loading}
                className="px-12 py-6 bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xl font-bold rounded-2xl hover:from-blue-600 hover:to-purple-600 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl"
              >
                {loading ? (
                  <span className="flex items-center gap-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                    <span>正在发现更多精彩奖励...</span>
                  </span>
                ) : (
                  <span className="flex items-center gap-3">
                    🎉
                    <span>发现更多奖励</span>
                    🎉
                  </span>
                )}
              </button>
            </div>
          )}

          {/* 统计信息 - 儿童友好版本 */}
          <div className="mt-8 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-2xl p-6 border border-blue-200 dark:border-blue-800">
            <div className="text-center mb-4">
              <div className="text-lg font-bold text-gray-800 dark:text-gray-200 mb-2">🎊 奖励统计</div>
            </div>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">{filteredRewards.length}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">所有奖励</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-green-600 dark:text-green-400">
                  {filteredRewards.filter(r => r.dkp_cost <= userDKP).length}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">可兑换</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-orange-600 dark:text-orange-400">
                  {filteredRewards.filter(r => r.dkp_cost > userDKP).length}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">努力中</div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* 添加动画样式 */}
      <style>{`
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(50px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default RewardList;